<script setup>
import { computed, reactive, ref, watch } from 'vue';
import AuthField from '@/components/auth/AuthField.vue';
import PasswordStrength from '@/components/auth/PasswordStrength.vue';
import UiButton from '@/components/ui/UiButton.vue';
import UserAvatar from '@/components/app/dashboard/UserAvatar.vue';
import { useAuthStore } from '@/store/auth/index.js';
import { fieldErrors, passwordChecks, requestError } from '@/utils/auth/password';
import { shrinkImage } from '@/utils/shrinkImage';

const auth = useAuthStore();
const user = computed(() => auth.user?.data ?? {});

// Spelled out here: not every browser carries Bosnian month names.
const MONTHS = ['januara', 'februara', 'marta', 'aprila', 'maja', 'juna', 'jula', 'augusta', 'septembra', 'oktobra', 'novembra', 'decembra'];
const memberSince = computed(() => {
  const date = user.value.created_at ? new Date(user.value.created_at) : null;
  return date && !Number.isNaN(date.getTime()) ? `${MONTHS[date.getMonth()]} ${date.getFullYear()}.` : '';
});

// ── Photo ──────────────────────────────────────────────────────────────────
const photoInput = ref(null);
const photoState = ref({ busy: false, error: '' });

async function changePhoto(work) {
  photoState.value = { busy: true, error: '' };
  try {
    await work();
    photoState.value = { busy: false, error: '' };
  } catch (e) {
    photoState.value = { busy: false, error: fieldErrors(e).profile_photo ?? requestError(e) };
  }
}

function onPhotoPicked(event) {
  const file = event.target.files?.[0];
  event.target.value = '';
  if (file) changePhoto(async () => auth.uploadProfilePhoto(await shrinkImage(file)));
}

// ── Personal details ───────────────────────────────────────────────────────
const profile = reactive({ first_name: '', last_name: '', email: '', current_password: '' });
const profileErrors = ref({});
const profileState = ref({ saving: false, saved: false, error: '' });

function resetProfile() {
  Object.assign(profile, { first_name: user.value.first_name ?? '', last_name: user.value.last_name ?? '', email: user.value.email ?? '', current_password: '' });
}
watch(user, resetProfile, { immediate: true });

const emailChanged = computed(() => profile.email.trim().toLowerCase() !== (user.value.email ?? '').toLowerCase());
const profileDirty = computed(
  () => emailChanged.value || profile.first_name.trim() !== (user.value.first_name ?? '') || profile.last_name.trim() !== (user.value.last_name ?? '')
);

async function saveProfile() {
  if (profileState.value.saving || !profileDirty.value) return;
  const errors = {};
  if (!profile.first_name.trim()) errors.first_name = 'Unesi ime.';
  if (!profile.last_name.trim()) errors.last_name = 'Unesi prezime.';
  if (!/^\S+@\S+\.\S+$/.test(profile.email.trim())) errors.email = 'Unesi ispravnu email adresu.';
  if (emailChanged.value && !profile.current_password) errors.current_password = 'Za promjenu email adrese unesi trenutnu šifru.';
  profileErrors.value = errors;
  if (Object.keys(errors).length) return;

  profileState.value = { saving: true, saved: false, error: '' };
  try {
    await auth.updateProfile({
      first_name: profile.first_name.trim(),
      last_name: profile.last_name.trim(),
      email: profile.email.trim(),
      ...(emailChanged.value ? { current_password: profile.current_password } : {}),
    });
    profileState.value = { saving: false, saved: true, error: '' };
  } catch (e) {
    profileErrors.value = fieldErrors(e);
    profileState.value = { saving: false, saved: false, error: Object.keys(profileErrors.value).length ? '' : requestError(e) };
  }
}
// "Saved" until the next edit.
const showSaved = computed(() => profileState.value.saved && !profileDirty.value);

// ── Password ───────────────────────────────────────────────────────────────
const password = reactive({ current_password: '', password: '', password_confirmation: '' });
const passwordErrors = ref({});
const passwordState = ref({ saving: false, saved: false, error: '', tried: false });

const passwordReady = computed(
  () => Boolean(password.current_password) && passwordChecks(password.password, password.password_confirmation).every((c) => c.ok)
);

async function savePassword() {
  passwordState.value.tried = true;
  if (passwordState.value.saving) return;
  const errors = {};
  if (!password.current_password) errors.current_password = 'Unesi trenutnu šifru.';
  if (password.password && password.password === password.current_password) errors.password = 'Nova šifra mora biti drugačija od trenutne.';
  passwordErrors.value = errors;
  if (Object.keys(errors).length || !passwordReady.value) return;

  passwordState.value = { saving: true, saved: false, error: '', tried: true };
  try {
    await auth.changePassword({ ...password });
    Object.assign(password, { current_password: '', password: '', password_confirmation: '' });
    passwordState.value = { saving: false, saved: true, error: '', tried: false };
  } catch (e) {
    passwordErrors.value = fieldErrors(e);
    passwordState.value = { saving: false, saved: false, error: Object.keys(passwordErrors.value).length ? '' : requestError(e), tried: true };
  }
}

const loggingOut = ref(false);
async function logout() {
  loggingOut.value = true;
  await auth.logout();
}
</script>

<template>
  <div class="h-full flex flex-col min-h-0 bg-[#fafbfc] relative overflow-hidden min-w-0">
    <header class="px-8 py-6 sticky top-0 z-20 bg-[#fafbfc]/90 backdrop-blur-md border-b border-[#1c4848]/5 shrink-0">
      <div class="max-w-[1600px] mx-auto">
        <div class="flex items-center gap-2 text-[#1c4848]/40 mb-1">
          <iconify-icon icon="solar:user-circle-linear" class="text-sm" aria-hidden="true" />
          <span class="text-[10px] font-bold uppercase tracking-wider">Nalog</span>
        </div>
        <h1 class="text-2xl font-serif font-medium text-[#1c4848] tracking-tight">Moj profil</h1>
      </div>
    </header>

    <div class="flex-1 min-h-0 overflow-y-auto custom-scrollbar px-4 sm:px-8 pt-8 pb-32">
      <div class="max-w-5xl mx-auto grid gap-8 lg:grid-cols-[300px_minmax(0,1fr)] items-start">
        <!-- Who is signed in -->
        <aside class="lg:sticky lg:top-8 rounded-[28px] bg-[#153838] text-white p-6 overflow-hidden relative">
          <div class="absolute -top-24 -right-20 w-64 h-64 rounded-full bg-[#2a7d78] opacity-40 blur-3xl" aria-hidden="true" />
          <div class="relative">
            <UserAvatar class="w-24 h-24 rounded-2xl font-display text-3xl shadow-xl shadow-black/20" />
            <input ref="photoInput" type="file" accept="image/jpeg,image/png,image/webp" class="sr-only" tabindex="-1" aria-label="Profilna slika" @change="onPhotoPicked" />
            <div class="mt-3 flex flex-wrap items-center gap-2">
              <button type="button" class="min-h-11 rounded-xl bg-white/10 px-4 text-xs font-bold text-white transition-colors hover:bg-white/20 disabled:opacity-50" :disabled="photoState.busy" @click="photoInput.click()">
                {{ photoState.busy ? 'Čuvam…' : (auth.getProfilePhoto ? 'Promijeni sliku' : 'Dodaj sliku') }}
              </button>
              <button v-if="auth.getProfilePhoto" type="button" class="min-h-11 rounded-xl px-3 text-xs font-bold text-white/60 transition-colors hover:text-white disabled:opacity-50" :disabled="photoState.busy" @click="changePhoto(() => auth.removeProfilePhoto())">Ukloni</button>
            </div>
            <p v-if="photoState.error" class="mt-2 text-xs text-red-200" role="alert">{{ photoState.error }}</p>
            <p class="mt-5 font-display text-xl font-medium leading-snug break-words">{{ auth.getName || 'Bez imena' }}</p>
            <p class="text-sm text-white/55 break-all">{{ user.email }}</p>
            <p v-if="memberSince" class="mt-4 text-xs text-white/40">Član tima od {{ memberSince }}</p>
            <div class="mt-6 pt-5 border-t border-white/10">
              <UiButton intent="inverse" block icon="solar:logout-3-linear" :loading="loggingOut" @click="logout">Odjavi se</UiButton>
            </div>
          </div>
        </aside>

        <div class="space-y-8 min-w-0">
          <!-- Personal details -->
          <section class="rounded-[28px] bg-white border border-[#1c4848]/5 shadow-sm p-6 sm:p-8" aria-labelledby="profile-details">
            <h2 id="profile-details" class="font-display text-xl font-semibold text-[#1c4848]">Lični podaci</h2>
            <p class="text-sm text-[#1c4848]/50 mt-1 mb-6">Ime i email koje vide ostali u timu. Na ovu adresu stiže i link za novu šifru.</p>

            <form class="space-y-5" novalidate @submit.prevent="saveProfile">
              <div class="grid sm:grid-cols-2 gap-5">
                <AuthField v-model="profile.first_name" label="Ime" autocomplete="given-name" :error="profileErrors.first_name" required />
                <AuthField v-model="profile.last_name" label="Prezime" autocomplete="family-name" :error="profileErrors.last_name" required />
              </div>
              <AuthField v-model="profile.email" label="Email" type="email" autocomplete="email" :error="profileErrors.email" required />

              <Transition enter-from-class="opacity-0 -translate-y-1" enter-active-class="transition duration-200" leave-to-class="opacity-0" leave-active-class="transition duration-150">
                <div v-if="emailChanged" class="rounded-2xl bg-deen-gold/15 border border-deen-gold/50 p-4 space-y-3">
                  <p class="flex items-start gap-2 text-xs text-[#1c4848]/75">
                    <iconify-icon icon="solar:shield-keyhole-linear" class="text-base shrink-0" aria-hidden="true" />
                    Mijenjaš email s kojim se prijavljuješ. Potvrdi da si to ti: unesi trenutnu šifru.
                  </p>
                  <AuthField v-model="profile.current_password" label="Trenutna šifra" type="password" autocomplete="current-password" :error="profileErrors.current_password" />
                </div>
              </Transition>

              <div class="flex flex-wrap items-center gap-3 pt-1">
                <UiButton type="submit" :loading="profileState.saving" :class="!profileDirty ? 'opacity-40 pointer-events-none' : ''" :aria-disabled="!profileDirty">Sačuvaj izmjene</UiButton>
                <UiButton v-if="profileDirty" intent="ghost" @click="resetProfile(); profileErrors = {}">Poništi</UiButton>
                <p v-if="showSaved" class="flex items-center gap-1.5 text-sm text-emerald-700" role="status">
                  <iconify-icon icon="solar:check-circle-linear" aria-hidden="true" />Sačuvano
                </p>
                <p v-if="profileState.error" class="text-sm text-red-700" role="alert">{{ profileState.error }}</p>
              </div>
            </form>
          </section>

          <!-- Password -->
          <section class="rounded-[28px] bg-white border border-[#1c4848]/5 shadow-sm p-6 sm:p-8" aria-labelledby="profile-password">
            <h2 id="profile-password" class="font-display text-xl font-semibold text-[#1c4848]">Promjena šifre</h2>
            <p class="text-sm text-[#1c4848]/50 mt-1 mb-6">Nakon promjene ostaješ prijavljen/a ovdje, a svi drugi uređaji i preglednici bit će odjavljeni.</p>

            <div v-if="passwordState.saved" class="mb-6 flex items-start gap-3 rounded-2xl bg-emerald-50 border border-emerald-200 px-4 py-3 text-sm text-emerald-800" role="status">
              <iconify-icon icon="solar:check-circle-linear" class="text-lg shrink-0 mt-px" aria-hidden="true" />
              Šifra je promijenjena. Svi drugi uređaji su odjavljeni.
            </div>

            <form class="space-y-5" novalidate @submit.prevent="savePassword">
              <input type="email" :value="user.email" autocomplete="username" class="sr-only" tabindex="-1" aria-hidden="true" readonly />
              <AuthField v-model="password.current_password" label="Trenutna šifra" type="password" autocomplete="current-password" :error="passwordErrors.current_password" required />
              <div class="grid sm:grid-cols-2 gap-5">
                <AuthField v-model="password.password" label="Nova šifra" type="password" autocomplete="new-password" :error="passwordErrors.password" required />
                <AuthField
                  v-model="password.password_confirmation"
                  label="Ponovi novu šifru"
                  type="password"
                  autocomplete="new-password"
                  :error="passwordState.tried && password.password_confirmation !== password.password ? 'Šifre se ne poklapaju.' : ''"
                  required
                />
              </div>
              <PasswordStrength :password="password.password" :confirmation="password.password_confirmation" />
              <div class="flex flex-wrap items-center gap-3 pt-1">
                <UiButton type="submit" :loading="passwordState.saving" :class="!passwordReady ? 'opacity-40' : ''" :aria-disabled="!passwordReady">Promijeni šifru</UiButton>
                <p v-if="passwordState.error" class="text-sm text-red-700" role="alert">{{ passwordState.error }}</p>
              </div>
            </form>
          </section>
        </div>
      </div>
    </div>
  </div>
</template>
